import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { message } from 'antd';
import { showModal } from '@/components/CommonModal';
import { MapStatus } from '../utils/config';
import { useEditorStore, useRoutePlanStore } from '../store';
import type { MapEditorCoordinator } from '../controller/MapEditorCoordinator';
import PolygonPopup from './PolygonPopup';
import { type NavigationEnumOptions } from '../utils/navigation';
import { MAP_EDITOR_CONFIRM_POPUP_Z_INDEX } from '../utils/zIndex';

interface MapEditorPopupPortalProps {
  /** Controller 引用，用于调用删除/更新状态/高亮/进入编辑等方法 */
  coordinatorRef: React.MutableRefObject<MapEditorCoordinator | null>;
  /** 弹窗内 Modal 的挂载容器 */
  getOverlayContainer: () => HTMLElement | null;
  /** 导航区枚举，用于弹窗展示拓扑关系/行驶方向 */
  navigationEnums?: NavigationEnumOptions;
}

/**
 * 地图多边形弹窗 Portal
 *
 * 职责：
 * 1. 订阅 editorStore.popupContent，当地图上选中多边形时把 React 组件渲染到
 *    百度地图自定义覆盖物创建的 DOM 节点（mountNode）中
 * 2. 封装弹窗内三个操作（删除、启用/停用、编辑）的确认交互
 * 3. 所有实际业务操作委托给 MapEditorCoordinator，自身只负责 UI 层确认框与 portal 渲染
 *
 * 为什么放在独立组件而非 index.tsx：
 * - 把"地图弹窗这一具体交互"从页面入口中剥离，降低 index.tsx 的耦合度
 * - portal 依赖的 coordinatorRef / getOverlayContainer 通过 props 注入，便于测试与复用
 */
export const MapEditorPopupPortal: React.FC<MapEditorPopupPortalProps> = ({
  coordinatorRef,
  getOverlayContainer,
  navigationEnums,
}) => {
  const popupContent = useEditorStore((s) => s.popupContent);

  /** 删除确认：弹出二次确认 Modal，确认后调用 Controller 删除 */
  const handleDelete = useCallback(
    (popupData: any) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      showModal({
        title: '',
        content: (
          <div style={{ textAlign: 'center' }}>
            您确定是否删除该动态地图区域？
          </div>
        ),
        container: getOverlayContainer(),
        zIndex: MAP_EDITOR_CONFIRM_POPUP_Z_INDEX,
        onOk: (cb) => {
          cb();
          ctrl.deletePolygon(popupData);
        },
      });
    },
    [coordinatorRef, getOverlayContainer]
  );

  /**
   * 编辑：关闭弹窗并进入编辑模式
   * 路径规划模式下禁止进入编辑绘制流程（与右侧列表编辑入口同一口径，见 useRightOperate）
   */
  const handleEdit = useCallback(
    (popupData: any) => {
      if (useRoutePlanStore.getState().planningMode) {
        message.warning('请先退出路径规划模式');
        return;
      }
      coordinatorRef.current?.enterEditMode(popupData);
    },
    [coordinatorRef]
  );

  /** 关闭详情弹窗 */
  const handleClose = useCallback(() => {
    coordinatorRef.current?.closePolygonPopup();
  }, [coordinatorRef]);

  /** 启用/停用确认：弹出二次确认 Modal，确认后切换状态并刷新弹窗/高亮 */
  const handleChangeStatus = useCallback(
    (popupData: any) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      const actionText =
        popupData.status === MapStatus.RS_INUSE ? '停用' : '启用';

      showModal({
        title: '',
        content: (
          <div style={{ textAlign: 'center' }}>
            是否确认{actionText}？该操作将影响车辆运行路线规划，请确定是否
            {actionText}
          </div>
        ),
        container: getOverlayContainer(),
        zIndex: MAP_EDITOR_CONFIRM_POPUP_Z_INDEX,
        onOk: (cb) => {
          cb();
          ctrl.updateMapStatus(popupData);
        },
      });
    },
    [coordinatorRef, getOverlayContainer]
  );

  if (!popupContent) {
    return null;
  }

  return createPortal(
    <PolygonPopup
      data={popupContent.data}
      onDelete={handleDelete}
      onEdit={handleEdit}
      onChangeStatus={handleChangeStatus}
      onClose={handleClose}
      getOverlayContainer={getOverlayContainer}
      navigationEnums={navigationEnums}
    />,
    popupContent.mountNode
  );
};
